import React from "react";
import { motion } from "framer-motion";
import { FaHandsHelping } from "react-icons/fa";
import { FiCalendar, FiMapPin } from "react-icons/fi";
import { Link } from "react-scroll";
import EmsiLionsLogo from "../assets/logolions.jpg";

const volunteering = [
    {
        role: "Club Membership Chairperson",
        org: "Lions Tanger EMSI",
        period: "Jul 2025 – Present",
        cause: "Humanitarian Aid",
        location: "Tangier, Morocco",
    },
    {
        role: "Member",
        org: "Lions Tanger EMSI",
        period: "Nov 2023 – Jun 2025",
        cause: "Humanitarian Aid",
        location: "Tangier, Morocco",
    },
];

const Volunteering = () => {
    return (
        <section
            id="volunteering"
            name="volunteering"
            className="w-full min-h-screen pt-24 md:pt-32 transition-colors duration-300
                 bg-gradient-to-b from-gray-50 via-white to-gray-100
                 dark:from-gray-900 dark:via-black dark:to-gray-800"
        >
            <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 md:py-20">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5 }}
                    className="mb-10 md:mb-14"
                >
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full overflow-hidden ring-2 ring-cyan-500/30 bg-white dark:bg-gray-800 flex items-center justify-center">
                            {/* Logo */}
                            <img
                                src={EmsiLionsLogo}
                                alt="EMSI Lions logo"
                                className="h-10 w-10 object-contain"
                            />
                        </div>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white inline-flex items-center gap-3">
                            <FaHandsHelping className="text-cyan-600 dark:text-cyan-400" />
                            Volunteering
                        </h2>
                    </div>
                    <span className="block mt-3 h-1 w-28 bg-gradient-to-r from-cyan-500 to-blue-600 rounded"></span>

                    <p className="mt-5 text-gray-700 dark:text-gray-300 text-base sm:text-lg max-w-3xl">
                        EMSI Lions is a movement dedicated to shaping a brighter future for women,
                        children, and our planet. Through empowering programs, dedicated volunteers,
                        and a global reach, we’re fostering positive change and hope in communities
                        worldwide.
                    </p>
                </motion.div>

                {/* Timeline */}
                <div className="relative ml-2">
                    {/* vertical line */}
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-cyan-500/30"></div>

                    <div className="space-y-8">
                        {volunteering.map((item, idx) => (
                            <motion.article
                                key={`${item.role}-${idx}`}
                                initial={{ x: -24, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.45, delay: idx * 0.06 }}
                                className="relative pl-6"
                            >
                                {/* node */}
                                <span className="absolute -left-2 top-2 inline-block h-3.5 w-3.5 rounded-full bg-cyan-500 ring-4 ring-cyan-500/20"></span>

                                {/* card */}
                                <div className="rounded-xl p-5 sm:p-6 border shadow-sm
                                bg-white/70 border-gray-200
                                dark:bg-gray-800/50 dark:border-gray-700
                                hover:border-cyan-400/60 hover:shadow-cyan-500/10 transition-all duration-300">
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                        <div className="flex items-start gap-3">
                                            <div className="h-10 w-10 rounded-full overflow-hidden ring-2 ring-cyan-500/20 bg-white dark:bg-gray-800 flex items-center justify-center">
                                                <img
                                                    src={EmsiLionsLogo}
                                                    alt="EMSI Lions logo"
                                                    className="h-8 w-8 object-contain"
                                                />
                                            </div>
                                            <div>
                                                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                                    {item.role}
                                                </h3>
                                                <p className="text-cyan-700 dark:text-cyan-400">{item.org}</p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap items-center gap-3 text-sm">
                                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                                <FiCalendar />
                                                <span>{item.period}</span>
                                            </div>
                                            {item.location && (
                                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                                    <FiMapPin />
                                                    <span>{item.location}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-3 text-gray-700 dark:text-gray-300">
                                        <p>
                                            Cause:{" "}
                                            <span className="font-medium text-cyan-700 dark:text-cyan-400">
                                                {item.cause}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="mt-16 md:mt-20 text-center"
                >
                    <Link
                        to="contact"
                        smooth
                        duration={500}
                        className="inline-block cursor-pointer bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3 rounded-lg font-medium text-white
                       hover:shadow-lg hover:shadow-cyan-500/20 active:scale-[0.98] transition-all duration-300"
                    >
                        Get in touch
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default Volunteering;